import { Activity, RefObject, useCallback, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getActiveChannel } from '@/store/slices/user/selectors';
import { EPermissions } from '@/types/permissions.types';
import { formatTime } from '@/utils/get-project-start-time';
import TableData from '@layouts/layout-table/layout-table-data';
import TableRow from '@layouts/layout-table/layout-table-row';
import Chip from '@styles/design-system/components/chips/chip';
import BrandliveAvatar from '@ui/brandlive-avatar/brandlive-avatar';
import { ICONS } from '@ui/icons/types';
import Popover from '@ui/popover/popover';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import GlobalContainer from '@ui/container/global-container';
import { updateSessionCrewAssociations } from '@/store/slices/project';
import { ISessionCrew } from '@/types/session.types';
import useParamsOrFirstSessionUuid from '@/utils/use-params-or-first-session-uuid';
import Container from '@ui/container/container';
import TextClippedTooltip from '@ui/text/text/text-clipped-tooltip';

type Props = {
  member: ISessionCrew;
  scrollRef: RefObject<HTMLDivElement | null>;
  inert?: boolean;
};

const CrewContentTableRow = ({ inert, member, scrollRef }: Props) => {
  const portalEl = useRef<HTMLDivElement | null>(null);

  const activeChannel = useAppSelector(getActiveChannel);

  const dispatch = useAppDispatch();

  const sessionUuid = useParamsOrFirstSessionUuid();

  const [popoverIsOpen, setPopoverIsOpen] = useState(false);

  const handleRemoveCrewMember = useCallback(() => {
    if (!sessionUuid) return;
    // remove crew member associations in db instead of updating crew list (should delete crew list from sessions table)
    dispatch(
      updateSessionCrewAssociations({
        sessionUuid,
        removedCrew: [member],
      })
    );
  }, [dispatch, member, sessionUuid]);

  if (!activeChannel || !member) return null;
  return (
    <TableRow
      key={member.id}
      backgroundColor={popoverIsOpen ? 'var(--global-cont-1)' : 'transparent'}
      hoverBackgroundColor="var(--global-cont-1)"
      focusWithinBackgroundColor="var(--global-cont-1)"
      border={0}
      inert={inert}
    >
      <TableData borderBottom="1px solid var(--global-cont-2)" width="200px">
        <div className="flex ai-center gap-8 margin-l-8">
          <div className="position-relative">
            <BrandliveAvatar profile={member.bl_admin?.profile} />
            <Activity mode={member?.bl_admin?.verified ? 'hidden' : 'visible'}>
              <GlobalContainer
                className="position-absolute radius-full flex ai-center pc-center square-14"
                css={{
                  top: 'calc(100% - 10px)',
                  left: 'calc(100% - 10px)',
                  background: 'var(--primitives-mode-1-orange-500)',
                  border: '1px solid var(--BG)',
                }}
              >
                <Icon name={ICONS.SEND_3} size={6} color="var(--local-bttn-sec-default-icotext)" />
              </GlobalContainer>
            </Activity>
          </div>
          <Text className="P2" clamp={1}>
            {`${member?.bl_admin?.profile?.first_name || ''} ${member?.bl_admin?.profile?.last_name || ''}`.trim()}
          </Text>
        </div>
      </TableData>
      <TableData borderBottom="1px solid var(--global-cont-2)" width="260px">
        <TextClippedTooltip
          textProps={{ className: 'P2', wordBreak: 'break-all' }}
          tooltipPortalProps={{ keepOpenOnContentHover: false, tailSize: 0 }}
          tooltipText={member?.bl_admin?.email || ''}
          text={member?.bl_admin?.email || ''}
        />
      </TableData>
      <TableData borderBottom="1px solid var(--global-cont-2)" width="240px">
        <div className="flex ai-center fw-nowrap gap-4">
          {member?.bl_admin?.channels?.[activeChannel]
            .filter((role) => [EPermissions.Producer, EPermissions.Presenter].includes(role as EPermissions))
            .toSorted()
            .map((role) => (
              <Chip key={role} label={role} textProps={{ transform: 'capitalize', clamp: 1 }} size={32} />
            ))}
        </div>
      </TableData>
      <TableData borderBottom="1px solid var(--global-cont-2)" width="264px">
        <Text className="P2">{member?.bl_admin?.last_login ? formatTime(member?.bl_admin?.last_login) : null}</Text>
      </TableData>
      <TableData borderBottom="1px solid var(--global-cont-2)" width="48px" padding={0}>
        <div className="flex jc-flex-end margin-r-8">
          <Popover
            portalEl={portalEl}
            width="fit-content"
            closeOnTabAway
            recalcutePosition={popoverIsOpen ? 1 : 0}
            trackOnWindowScroll
            trackScrollElRef={scrollRef}
            cycleWithArrowKeys
            onOpen={() => setPopoverIsOpen(true)}
            onClose={() => setPopoverIsOpen(false)}
            renderTriggerButton={({ open }) => (
              <Button
                design="LocalBttnSec"
                fixedState={open ? 'active' : undefined}
                height="32px"
                width="32px"
                className="flex-column ai-center jc-center"
              >
                <Icon name={ICONS.THREE_DOTS_HORIZONTAL} size={16} />
              </Button>
            )}
          >
            {({ triggerBounds }) => {
              const triggerWidth = triggerBounds?.width || 32;
              const leftPosition = triggerBounds?.left || 0;
              const transform = `translate(${`calc(-100% + ${triggerWidth}px)`}, ${(triggerBounds?.height || 32) + 2}px)`;
              return (
                <Container
                  width="fit-content"
                  className="position-fixed z-20 overflow-y-auto flex-column"
                  borderRadius="12px"
                  style={{
                    top: triggerBounds?.top || 0,
                    left: leftPosition,
                    transform,
                  }}
                >
                  <Button
                    design="LocalBttnSec"
                    borderRadius="0"
                    width="100%"
                    height="100%"
                    onClick={handleRemoveCrewMember}
                  >
                    <Text whiteSpace="nowrap" color="var(--global-error-500)" className="P2 flex ai-center gap-8">
                      <Icon name={ICONS.DELETE_TRASH_BIN} />
                      <span>Remove</span>
                    </Text>
                  </Button>
                </Container>
              );
            }}
          </Popover>
        </div>
      </TableData>
    </TableRow>
  );
};

export default CrewContentTableRow;
